import type {
  ScorecardAllRowsListState,
  ScorecardCriterionRowsListState,
  ScorecardRowVerdictFilter,
  ScorecardViewState,
} from "../types";
import ScoreSidebarConfiguration from "../../ScoreSidebarConfiguration";
import {
  buildDraftFromScoreConfiguration,
  buildPayloadFromDraft,
  getDefaultScoreColumnIds,
} from "../../utils";
import { mapCriterionFilterToRowFilter } from "../utils";

import ScorecardEvaluatorSwitcher from "./ScorecardEvaluatorSwitcher";
import AddCriterionView from "./Overview/AddCriterionView";
import AllRowsView from "./Rows/AllRowsView";
import CreateView from "./CreateView";
import CriterionDetailView from "./Detail/CriterionDetailView";
import EditCriterionView from "./EditCriterionView";
import OverviewView from "./Overview/OverviewView";
import RowDetailView from "./Detail/RowDetailView";

import type { ScorecardDisplayCriterion } from "@/routes/smart-table/stores/scorecard-store";
import type { ScoreResult } from "@/queries/smart-tables";
import type {
  ConfigureScorecardRequest,
  CriterionSummary,
  GetScorecardRowResponse,
  Scorecard,
  ScorecardRowSummary,
  ScorecardStep,
} from "@/queries/smart-tables/scorecard";
import type { SmartCell, SmartColumn } from "@/queries/smart-tables/types";

const EVALUATOR_COLUMN_TYPES = new Set([
  "CONTAINS",
  "REGEX",
  "ASSERT_VALID",
  "COMPARE",
  "LLM_ASSERTION",
  "CODE_EXECUTION",
  "MAX_TOOL_CALLS",
  "NO_INVALID_TOOL_CALLS",
  "COUNT",
  "TRAJECTORY",
]);

const buildLegacyConversionNotices = (
  scoreConfiguration: ScoreResult["score_configuration"] | null | undefined,
  columns: SmartColumn[],
): string[] => {
  if (!scoreConfiguration) {
    return [];
  }

  const scoreType = scoreConfiguration.score_type;
  const scoreConfig = scoreConfiguration.score_config ?? {};

  if (scoreType === "custom") {
    return [
      "Custom scoring code cannot be converted automatically. Create an empty scorecard and add equivalent evaluators manually.",
    ];
  }

  if (scoreType === "aggregate") {
    return [
      "Winner / aggregate scoring converts to a column evaluator. Review the selected column and evaluation method after conversion.",
    ];
  }

  const configuredColumnIds = Array.isArray(scoreConfig.column_ids)
    ? scoreConfig.column_ids.map(columnId => String(columnId))
    : [];
  const columnsById = new Map(columns.map(column => [column.id, column]));
  const selectedColumns = configuredColumnIds
    .map(columnId => columnsById.get(columnId))
    .filter((column): column is SmartColumn => Boolean(column));
  const unsupportedColumns = selectedColumns.filter(
    column => !EVALUATOR_COLUMN_TYPES.has(column.type),
  );

  const notices: string[] = [];
  if (scoreType === "numeric") {
    notices.push(
      "Numeric score columns convert to column evaluators and assume values are already on a 0–1 score scale. Edit the evaluator if your scores use a 0–100, 1–5, cost, or latency scale.",
    );
  }
  if (scoreType === "boolean" && unsupportedColumns.length > 0) {
    notices.push(
      "Boolean token scoring imports the selected columns directly. Review any text columns to make sure their values read as true/false or pass/fail.",
    );
  }
  if (scoreType === "auto" && unsupportedColumns.length > 0) {
    notices.push(
      "Auto scoring imports selected columns directly. Review plain text or telemetry columns to make sure their values are score-like.",
    );
  }
  if (
    scoreType === "boolean" &&
    scoreConfig.assertion_aggregation &&
    scoreConfig.assertion_aggregation !== "mean"
  ) {
    notices.push(
      "LLM assertion evaluators use scorecard thresholds and may not exactly match legacy all/any assertion aggregation semantics.",
    );
  }

  return notices;
};

interface ScorecardViewProps {
  isLoading: boolean;
  scorecard: Scorecard | null;
  hasSimpleScore: boolean;
  legacyScoreResult?: ScoreResult | null;
  configureIsPending: boolean;
  migrateLegacyIsPending: boolean;
  onCreateScorecard: () => void;
  onMigrateLegacyScorecard: () => void;
  view: ScorecardViewState;
  rows: ScorecardRowSummary[];
  totalRows?: number;
  /** Verdict counts across the whole calculation (aggregate verdicts). */
  rowsVerdictCounts?: Record<string, number>;
  /** Verdict counts across the whole calculation for the active step. */
  criterionVerdictCounts?: Record<string, number>;
  getRowLabel?: (rowIndex: number) => string;
  onOpenAllRows: (filter?: ScorecardRowVerdictFilter) => void;
  onOpenAllRowsRow: (rowIndex: number) => void;
  onRerunRow: (rowIndex: number) => void;
  getIsRowRerunning: (rowIndex: number) => boolean;
  isRerunDisabled: boolean;
  onLoadMoreRows?: () => void;
  hasMoreRows?: boolean;
  isLoadingMoreRows?: boolean;
  allRowsListState: ScorecardAllRowsListState;
  onAllRowsListStateChange: (nextState: Partial<ScorecardAllRowsListState>) => void;
  criterionRowsListState: ScorecardCriterionRowsListState;
  onCriterionRowsListStateChange: (nextState: Partial<ScorecardCriterionRowsListState>) => void;
  activeStep?: ScorecardStep;
  activeSummary?: CriterionSummary;
  rowBreakdowns: Record<number, GetScorecardRowResponse | undefined>;
  columns: SmartColumn[];
  onOpenCriterionRow: (rowIndex: number) => void;
  onEditCriterion: (stepId: string) => void;
  onRerunCriterionRows: (rowIndices?: number[]) => void;
  fallbackRerunRowIndices: number[];
  isCriterionRerunning: boolean;
  isCriterionRowsLoading?: boolean;
  isActiveCriterionStale?: boolean;
  getBackingCell?: (
    rowIndex: number,
    backingColumnId: string | null | undefined,
  ) => SmartCell | undefined;
  onSave: (payload: ConfigureScorecardRequest) => void;
  newCriterionDraftStep: ScorecardStep | null;
  activeRowIndex?: number;
  activeRowBreakdown?: GetScorecardRowResponse;
  getRowCellValue?: (rowIndex: number, columnId: string) => string | null;
  displayCriteria: ScorecardDisplayCriterion[];
  getIsCriterionRerunning: (stepId: string) => boolean;
  getIsCriterionStale: (stepId: string) => boolean;
  onRerunCriterion: (stepId: string) => void;
  onOpenFailedRows: (stepId: string, filter?: "fail" | "error") => void;
  onOpenCriterion: (stepId: string) => void;
  onAddRowCriterion: () => void;
  onSelectAddCriterion: (primitiveType: string, scope: "all" | "row" | "scorecard-wide") => void;
  onCloseAddCriterion: () => void;
  isReadOnly?: boolean;
}

const ScorecardView = ({
  isLoading,
  scorecard,
  hasSimpleScore,
  legacyScoreResult,
  configureIsPending,
  migrateLegacyIsPending,
  onCreateScorecard,
  onMigrateLegacyScorecard,
  view,
  rows,
  totalRows,
  rowsVerdictCounts,
  criterionVerdictCounts,
  getRowLabel,
  onOpenAllRows,
  onOpenAllRowsRow,
  onRerunRow,
  getIsRowRerunning,
  isRerunDisabled,
  onLoadMoreRows,
  hasMoreRows = false,
  isLoadingMoreRows = false,
  allRowsListState,
  onAllRowsListStateChange,
  criterionRowsListState,
  onCriterionRowsListStateChange,
  activeStep,
  activeSummary,
  rowBreakdowns,
  columns,
  onOpenCriterionRow,
  onEditCriterion,
  onRerunCriterionRows,
  fallbackRerunRowIndices,
  isCriterionRerunning,
  isCriterionRowsLoading,
  isActiveCriterionStale,
  getBackingCell,
  onSave,
  newCriterionDraftStep,
  activeRowIndex,
  activeRowBreakdown,
  getRowCellValue,
  displayCriteria,
  getIsCriterionRerunning,
  getIsCriterionStale,
  onRerunCriterion,
  onOpenFailedRows,
  onOpenCriterion,
  onAddRowCriterion,
  onSelectAddCriterion,
  onCloseAddCriterion,
  isReadOnly = false,
}: ScorecardViewProps) => {
  if (isLoading && !scorecard) {
    return <div className="text-sm text-muted-foreground">Loading scorecard…</div>;
  }

  if (!scorecard) {
    if (hasSimpleScore) {
      const legacyScoreConfiguration = legacyScoreResult?.score_configuration ?? null;
      const defaultScoreColumnIds = getDefaultScoreColumnIds(columns);
      const legacyDraft = buildDraftFromScoreConfiguration(
        legacyScoreConfiguration,
        defaultScoreColumnIds,
      );
      const legacyDraftEvaluation = buildPayloadFromDraft(legacyDraft, defaultScoreColumnIds);
      const conversionNotices = buildLegacyConversionNotices(legacyScoreConfiguration, columns);

      return (
        <div className="space-y-4">
          <CreateView
            isSaving={configureIsPending || migrateLegacyIsPending}
            hasSimpleScore={hasSimpleScore}
            conversionNotices={conversionNotices}
            onCreate={onCreateScorecard}
            onConvertLegacy={onMigrateLegacyScorecard}
          />
          <ScoreSidebarConfiguration
            draft={legacyDraft}
            columns={columns}
            defaultScoreColumnIds={defaultScoreColumnIds}
            draftEvaluation={legacyDraftEvaluation}
            autosaveState="idle"
            autosaveError={null}
            isInitialLoading={false}
            isRecalculating={false}
            isReadOnly
          />
        </div>
      );
    }

    return (
      <div className="rounded-lg border border-border/70 bg-muted/10 px-3 py-4 text-sm text-muted-foreground">
        Preparing an empty scorecard…
      </div>
    );
  }

  // Evaluators that the scope switcher can jump between. Column aggregate
  // evaluators use an aggregate-first detail view, but should still be navigable
  // from the same evaluator selector for a consistent scorecard browsing model.
  const evaluatorSwitcherOptions = displayCriteria
    .filter(item => !item.isRemoved && item.step)
    .map(item => ({
      stepId: item.stepId,
      title: item.title ?? item.step?.title ?? item.summary?.title ?? "Evaluator",
      failCount: item.summary
        ? (item.summary.fail_row_count ?? 0) + item.summary.error_row_count
        : 0,
    }));

  const showEvaluatorSwitcher =
    evaluatorSwitcherOptions.length > 0 && (view.view === "all-rows" || view.view === "criterion");

  // Switching scope from the switcher carries the current row filter over to
  // the new scope so the user doesn't lose their place (e.g. staying on
  // "Failed" while moving from one evaluator to another, or to "All
  // evaluators").
  const handleSelectRowScope = (stepId: string | null) => {
    if (stepId === null) {
      const carryFilter =
        view.view === "criterion"
          ? mapCriterionFilterToRowFilter(criterionRowsListState.filter)
          : undefined;
      onOpenAllRows(carryFilter);
      return;
    }
    if (view.view === "all-rows") {
      onCriterionRowsListStateChange({ filter: allRowsListState.filter, search: "" });
    }
    onOpenCriterion(stepId);
  };

  const evaluatorSwitcher = showEvaluatorSwitcher ? (
    <ScorecardEvaluatorSwitcher
      options={evaluatorSwitcherOptions}
      selectedStepId={view.view === "criterion" ? (activeStep?.id ?? null) : null}
      onSelect={handleSelectRowScope}
    />
  ) : null;

  const renderBody = () => {
    switch (view.view) {
      case "all-rows":
        return (
          <AllRowsView
            titleSlot={evaluatorSwitcher}
            rows={rows}
            verdictCounts={rowsVerdictCounts}
            getRowLabel={getRowLabel}
            onOpenRow={onOpenAllRowsRow}
            onRerunRow={onRerunRow}
            getIsRowRerunning={getIsRowRerunning}
            isRerunDisabled={isReadOnly || isRerunDisabled}
            onLoadMoreRows={onLoadMoreRows}
            hasMoreRows={hasMoreRows}
            isLoadingMoreRows={isLoadingMoreRows}
            listState={allRowsListState}
            onListStateChange={onAllRowsListStateChange}
          />
        );
      case "criterion":
        return activeStep ? (
          <CriterionDetailView
            titleSlot={evaluatorSwitcher}
            step={activeStep}
            summary={activeSummary}
            rows={rows}
            verdictCounts={criterionVerdictCounts}
            rowBreakdowns={rowBreakdowns}
            columns={columns}
            totalRows={totalRows}
            getRowLabel={getRowLabel}
            rowListState={criterionRowsListState}
            onRowListStateChange={onCriterionRowsListStateChange}
            onOpenRow={onOpenCriterionRow}
            onEditCriterion={() => onEditCriterion(activeStep.id)}
            onRerunRows={onRerunCriterionRows}
            fallbackRerunRowIndices={fallbackRerunRowIndices}
            isRerunning={isCriterionRerunning}
            isRerunDisabled={isReadOnly || isRerunDisabled}
            isReadOnly={isReadOnly}
            isLoadingRows={isCriterionRowsLoading}
            onLoadMoreRows={onLoadMoreRows}
            hasMoreRows={hasMoreRows}
            isLoadingMoreRows={isLoadingMoreRows}
            isStale={isActiveCriterionStale}
            getBackingCell={getBackingCell}
          />
        ) : null;
      case "criterion-edit":
        if (isReadOnly) {
          return null;
        }
        return activeStep ? (
          <EditCriterionView
            scorecard={scorecard}
            step={activeStep}
            columns={columns}
            isSaving={configureIsPending}
            onSave={onSave}
            onRemove={onSave}
          />
        ) : null;
      case "add-criterion":
        if (isReadOnly) {
          return null;
        }
        return (
          <AddCriterionView
            scope={view.scope}
            onSelect={item => onSelectAddCriterion(String(item.primitiveType), view.scope ?? "all")}
            onClose={onCloseAddCriterion}
          />
        );
      case "criterion-new":
        if (isReadOnly) {
          return null;
        }
        return scorecard && newCriterionDraftStep ? (
          <EditCriterionView
            mode="create"
            scorecard={scorecard}
            step={newCriterionDraftStep}
            columns={columns}
            isSaving={configureIsPending}
            onSave={onSave}
            onRemove={onSave}
          />
        ) : null;
      case "row":
        return (
          <RowDetailView
            rowIndex={activeRowIndex ?? view.rowIndex}
            rowBreakdown={activeRowBreakdown}
            steps={scorecard.steps}
            getRowLabel={getRowLabel}
            getColumnLabel={columnId =>
              columns.find(column => column.id === columnId)?.title ?? columnId
            }
            getRowCellValue={getRowCellValue}
            focusedStepId={view.fromStepId}
            getBackingCell={getBackingCell}
            onRerunRow={onRerunRow}
            isRerunning={getIsRowRerunning(view.rowIndex)}
            isRerunDisabled={isReadOnly || isRerunDisabled}
            isReadOnly={isReadOnly}
          />
        );
      case "overview":
      default:
        return (
          <OverviewView
            displayCriteria={displayCriteria}
            columns={columns}
            isRerunDisabled={isReadOnly || isRerunDisabled}
            getIsCriterionRerunning={getIsCriterionRerunning}
            getIsCriterionStale={getIsCriterionStale}
            onRerunCriterion={onRerunCriterion}
            onOpenFailedRows={onOpenFailedRows}
            onOpenCriterion={onOpenCriterion}
            onEditCriterion={onEditCriterion}
            onAddRowCriterion={onAddRowCriterion}
            isReadOnly={isReadOnly}
          />
        );
    }
  };

  return renderBody();
};

export default ScorecardView;
